export type APIId = string | number;

export interface KeyDate {
  id: APIId;
  name: string;
  date: string;
  description?: string;
  old_progress?: number;
  progress: number;
}

export interface Question {
  id: APIId;
  body: string;
  type?: QuestionType;
  answers: any[];
  user_answer?: any;
  quiz_id?: APIId;
  matching_pairs_data?: { term: string; definition: string }[];
  correct_answer?: string;
}

export interface ChatMessage {
  text?: string;
  // Uploaded files
  files?: UploadFile[];
}

export interface UploadFile {
  signed_id: string;
  filename?: string;
  type?: string;
  content_type?: string;
  byte_size?: number;
  size?: number;
  url?: string;
}

export interface APIChatMessage {
  id?: APIId;
  role: 'user' | 'assistant';
  text?: string;
  file?: UploadFile;
}

export interface User {
  id: APIId;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email: string;
  avatar?: string;
  has_password?: boolean;
  push_notifications_enabled?: boolean;
  email_notifications?: boolean;
  email_updates?: boolean;
}

export interface UserNotificationSettings {
  id?: APIId;
  browser_notifications: boolean;
  email_notifications: boolean;
  email_updates: boolean;
}

export type MaterialCompletionStatus = 'not_started' | 'in_progress' | 'completed';

export interface Material {
  id: APIId;
  title: string;
  folder_id: APIId;
  completed: boolean;
  completion_status: MaterialCompletionStatus;
  completed_by_user_at: string;
}

export interface SourceLink {
  id: APIId;
  url: string;
}

export interface Note {
  id: APIId;
  title: string;
  material_id?: APIId; // The ID of the material that contains this note
  file_sources: string[];
  link_sources: SourceLink[];
  content?: string;
  share_token?: string;
}


export interface CardSet {
  id: string;
  material_id: string;
  title: string;
  quantity: number;
  language: string;
  skipping_hints?: boolean;
  cards: Card[];
  created_at: string;
  updated_at: string;
  processing: boolean;
  expired: boolean;
}

export interface Card {
  card_set_id: string;
  id: string;
  term: string;
  definition: string;
  hint?: string;
  status: 'created' | 'in_progress' | 'learned';
  times_reviewed: number;
  last_reviewed_at?: string;
  times_remembered: number;
  last_remembered_at?: string;
  created_at: string;
  updated_at: string;
}

export type TimeLimitOption = 'no_limit' | '5' | '10' | '20' | 'custom';
export type QuestionType = 'multiple_choice' | 'single_answer' | 'matching_pairs' | 'true_false' | 'fill_in_the_blank';

export const QUESTION_TYPES = {
  MULTIPLE_CHOICE: 'multiple_choice' as const,
  SINGLE_ANSWER: 'single_answer' as const,
  MATCHING_PAIRS: 'matching_pairs' as const,
  TRUE_FALSE: 'true_false' as const,
  FILL_IN_THE_BLANK: 'fill_in_the_blank' as const,
} as const;

export const QUESTION_TYPES_ARRAY = Object.values(QUESTION_TYPES);

export type QuizSetupData = {
  timeLimit: null | number;
  questions: Array<{question_type: QuestionType; qty: number}>;
  questionQuantities?: {[key in QuestionType]?: number};
  adaptiveMix: boolean;
  skippingHints: boolean;
  prompt: string;
  note: Note | null;
  files: string[];
  links: string[];
  folder_id?: string | number;
  max_questions: number;
  language?: string;
}

export interface Quiz {
  id: APIId;
}

export interface Folder {
  id: APIId;
  title: string;
}

export interface Course {
  id: APIId;
  title: string;
}

export interface PodcastSectionActor {
  avatar: string;
  name: string;
  text: string;
}

export interface PodcastSection {
  title: string;
  start: number;
  actors: PodcastSectionActor[];
}

export interface Podcast {
  audio: string;
  title: string;
  summary: string;
  sections: PodcastSection[];
}

export interface Link {
  id: APIId;
  url: string;
}

export enum UnitState {
  Done = 'done',
  Locked = 'locked',
  Unlocked = 'unlocked'
}

export interface Unit {
  id: APIId;
  title: string;
  state: UnitState;
  start_date: string;
  description: string;
  content_material: Material & { url: string };
}

export interface SubUnit {
  id: APIId;
  title: string;
  start_date: string;
  description: string;
  files?: UploadFile[];
}
